import { getLTSCountryCode, dateSigned, getAddressType } from '../helpers';

const trimObjectValuesWhiteSpace = (key, value) => {
  if (typeof value === 'string') {
    return value.trim();
  }
  return value;
};

const getNotificationMethod = notificationMethod => {
  switch (notificationMethod) {
    case 'Yes, send me text message notifications':
    case 'text':
      return 'TEXT';
    case 'No, just send me email notifications':
    case 'email':
      return 'EMAIL';
    default:
      return 'NONE';
  }
};

// Maps user-facing letter notification preferences to OptInMethod enum values.
// Note: NONE is translated to PAPER by vets-service for claimant-service persistence.
const getLettersNotificationMethod = lettersNotificationMethod => {
  switch (lettersNotificationMethod) {
    case 'text and email':
      return 'TEXT_AND_EMAIL';
    case 'text':
      return 'TEXT';
    case 'email':
      return 'EMAIL';
    case 'mail':
      return 'NONE';
    default:
      return 'NONE';
  }
};

export function transform(_formConfig, form) {
  const { data } = form;

  // Extract applicant name
  const applicantFullName = data?.applicantFullName || {};

  // Extract mailing address
  const mailingAddress = data?.mailingAddress || {};

  // Extract contact info
  const contactInfo = data?.contactInfo || {};

  // Build training providers array
  const trainingProviders = (data?.trainingProviders || []).map(provider => ({
    providerName: provider?.providerName,
    providerAddress: {
      street: provider?.providerAddress?.street,
      // Ensure street2 is always a string (empty string if undefined/null)
      // to prevent address blending in backend when optional fields are missing
      street2: provider?.providerAddress?.street2 || '',
      city: provider?.providerAddress?.city,
      state: provider?.providerAddress?.state,
      postalCode: provider?.providerAddress?.postalCode,
      country: getLTSCountryCode(provider?.providerAddress?.country),
    },
  }));

  const notificationMethod = data?.mebDole210Updates
    ? data?.monthlyEnrollmentVerificationMethod
    : data?.['view:receiveTextMessages']?.receiveTextMessages;

  // Build the payload
  const payload = {
    formId: form?.formId,
    '@type': 'VetTecSubmission',
    claimant: {
      claimantId: data?.claimantId,
      firstName: applicantFullName?.first,
      lastName: applicantFullName?.last,
      middleName: applicantFullName?.middle,
      suffix: applicantFullName?.suffix,
      dateOfBirth: data?.dateOfBirth,
      contactInfo: {
        addressLine1: mailingAddress?.street,
        // Ensure addressLine2 is always a string (empty string if undefined/null)
        // to prevent address blending in backend when optional fields are missing
        addressLine2: mailingAddress?.street2 || '',
        addressType: getAddressType(mailingAddress),
        city: mailingAddress?.city,
        stateCode: mailingAddress?.state,
        zipcode: mailingAddress?.postalCode,
        countryCode: getLTSCountryCode(mailingAddress?.country),
        emailAddress: contactInfo?.emailAddress?.toLowerCase(),
        mobilePhoneNumber: contactInfo?.mobilePhone?.contact,
        homePhoneNumber: contactInfo?.homePhone?.contact,
      },
      preferredContact: data?.contactMethod,
      notificationMethod: getNotificationMethod(notificationMethod),
      // Only include lettersNotificationMethod when mebDole210Updates flag is enabled
      ...(data?.mebDole210Updates && {
        lettersNotificationMethod: getLettersNotificationMethod(
          data?.educationLettersNotificationMethod,
        ),
      }),
    },
    militaryInfo: {
      dutyRequirementMet: data?.dutyRequirementMet,
      dateReleasedFromActiveDuty: data?.dateReleasedFromActiveDuty,
      activeDutyDuringHitechVets: data?.activeDutyDuringHitechVets,
    },
    directDeposit: {
      directDepositAccountType: data?.bankAccount?.accountType,
      directDepositAccountNumber: data?.bankAccount?.accountNumber,
      directDepositRoutingNumber: data?.bankAccount?.routingNumber,
    },
    trainingProviders: {
      providers: trainingProviders,
      plannedStartDate: data?.plannedStartDate || null,
    },
    employmentInfo: {
      isEmployed: data?.isEmployed || false,
      isInTechnologyIndustry: data?.isInTechnologyIndustry || false,
      technologyAreaOfFocus: data?.technologyAreaOfFocus,
      currentSalary: data?.currentSalary,
      highestLevelOfEducation: data?.highestLevelOfEducation,
    },
    attestationAgreementAccepted: data?.privacyAgreementAccepted || false,
    dateSigned: dateSigned(),
  };

  return JSON.stringify(payload, trimObjectValuesWhiteSpace, 4);
}
